import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { baseUrl } from '../../constant/config';
import { PLATFORM_META } from './shareMeta';

// Instagram rejects captions over 2200 characters; Facebook is far more
// generous, so one limit covers both.
const CAPTION_LIMIT = 2200;

/**
 * Edit the caption of a post that is already live.
 *
 * Facebook only. Instagram captions are fixed once published - Meta exposes no
 * endpoint to change them - so for Instagram this explains the situation
 * instead of offering an edit that could not take effect.
 *
 * `target` comes from either tab:
 *   { mode: 'live',   platform, postId,  caption, title }
 *   { mode: 'record', platform, shareId, caption, title }
 * A live post is already showing Meta's own text, so only the record view has
 * to go and read the current caption before offering to change it.
 */
export default function EditCaptionDialog({ open, onClose, target, onDone }) {
  const queryClient = useQueryClient();
  const [caption, setCaption] = useState('');

  const platform = PLATFORM_META[target?.platform] || {};
  const editable = platform.canEditCaption;
  const isRecord = target?.mode === 'record';

  const {
    data: live,
    isLoading: liveLoading,
    isError: liveError,
    error: liveErrorObj,
    refetch: refetchLive,
  } = useQuery(
    ['shareLive', target?.shareId],
    async () => {
      const { data } = await axios.get(`${baseUrl}/api/videos/shares/${target.shareId}/live`);
      return data.data.live;
    },
    {
      enabled: Boolean(open && isRecord && target?.shareId && target?.postId),
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  useEffect(() => {
    if (!open) return;
    // Prefer what the platform reports; fall back to the stored copy while the
    // live read is still in flight or unavailable.
    setCaption(live?.caption ?? target?.caption ?? '');
  }, [open, target, live]);

  const {
    mutate: save,
    isLoading: saving,
    isError: saveFailed,
    error: saveError,
    reset: resetSave,
  } = useMutation(
    async () => {
      const url = isRecord
        ? `${baseUrl}/api/videos/shares/${target.shareId}`
        : `${baseUrl}/api/videos/platform-posts/${encodeURIComponent(target.postId)}`;
      const body = isRecord ? { caption } : { platform: target.platform, caption };
      const { data } = await axios.patch(url, body);
      return data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('platformPosts');
        queryClient.invalidateQueries('platformShares');
        queryClient.invalidateQueries('recordings');
        if (target?.shareId) queryClient.invalidateQueries(['shareLive', target.shareId]);
        onDone?.(data.message || 'Caption updated');
        onClose();
      },
    }
  );

  useEffect(() => {
    if (open) resetSave();
  }, [open, resetSave]);

  const unchanged = caption === (live?.caption ?? target?.caption ?? '');
  const gone = live && live.exists === false;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          {platform.icon}
          <span>Edit {platform.label || 'post'} caption</span>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          {target?.title || `${platform.label} post`}
        </Typography>

        {!editable && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Instagram captions cannot be edited</AlertTitle>
            Meta provides no API for changing the caption of a post that is already live. To change
            this text the post has to be deleted and published again.
          </Alert>
        )}

        {liveLoading && (
          <Box sx={{ mb: 2 }}>
            <Skeleton height={24} width="40%" />
            <Skeleton height={18} width="70%" />
          </Box>
        )}

        {liveError && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => refetchLive()}>
                Retry
              </Button>
            }
          >
            Could not read the live post from Meta:{' '}
            {liveErrorObj?.response?.data?.message || liveErrorObj?.message}. The stored caption is
            shown instead.
          </Alert>
        )}

        {gone && (
          <Alert severity="error" sx={{ mb: 2 }}>
            This post is no longer on {platform.label}. It has been marked deleted.
          </Alert>
        )}

        {live?.exists && (
          <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            {typeof live.likeCount === 'number' && (
              <Chip size="small" label={`${live.likeCount} likes`} />
            )}
            {typeof live.commentCount === 'number' && (
              <Chip size="small" label={`${live.commentCount} comments`} />
            )}
            {live.permalink && (
              <Chip
                size="small"
                clickable
                component={Link}
                href={live.permalink}
                target="_blank"
                rel="noreferrer"
                label="View post"
              />
            )}
          </Stack>
        )}

        <TextField
          label="Caption"
          multiline
          minRows={4}
          fullWidth
          value={caption}
          disabled={!editable || saving || liveLoading || gone}
          onChange={(e) => setCaption(e.target.value.slice(0, CAPTION_LIMIT))}
          helperText={`${caption.length} / ${CAPTION_LIMIT}`}
        />

        {saveFailed && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {saveError?.response?.data?.message || saveError?.message}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => save()}
          disabled={!editable || saving || unchanged || liveLoading || gone}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {saving ? 'Saving...' : 'Save caption'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

EditCaptionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  target: PropTypes.object,
  onDone: PropTypes.func,
};
